// Made-up shipments for judging the design (see lib/sample.ts). Real shipments live in the Serb Mind vault.
import { sampleOnly } from '../../lib/sample';
import type { Tone } from '../../components/tone';
import { rule, daysSince } from '../../lib/rules';

export type { Detail, Mode, Party, Place, Shipment, Stage, TimelineEvent } from '@serb/shared';
import type { Detail, Place, Shipment, Stage } from '@serb/shared';

export const STAGE_TONE: Record<Stage, Tone> = {
  RFQ: 'neutral',
  'Rates Requested': 'warning',
  'Rates Received': 'warning',
  Quoting: 'info',
  Booked: 'info',
  'In Transit': 'success',
  'Pre-arrival': 'neutral',
  Delayed: 'error',
  Arrived: 'success',
  Completed: 'neutral',
  Lost: 'error',
  Cancelled: 'neutral',
  Merged: 'neutral',
};

/** The groups of stages that make up the tabs "On Hand", "Inquiry", "Arrived" and "Lost" */
export const STAGE_GROUPS: { label: string; stages: Stage[] }[] = [
  { label: 'On Hand', stages: ['Booked', 'In Transit', 'Pre-arrival', 'Delayed'] },
  { label: 'Inquiry', stages: ['RFQ', 'Rates Requested', 'Rates Received', 'Quoting'] },
  { label: 'Arrived', stages: ['Arrived', 'Completed'] },
  { label: 'Lost', stages: ['Lost', 'Cancelled', 'Merged'] },
];

/** The tabs along the top of the list. "Active" is what is still being worked on (on hand and inquiry). */
export const SHIPMENT_TABS = ['Active', 'On Hand', 'Inquiry', 'Arrived', 'Lost', 'Needs Attention'] as const;
export type ShipmentTab = (typeof SHIPMENT_TABS)[number];

/** The seven steps of the tracker on a shipment's page */
export const STEPS = ['RFQ', 'Rates', 'Quotation', 'Booking', 'In Transit', 'Arrived', 'Completed'] as const;
const STEP_OF: Record<Stage, number> = {
  RFQ: 0,
  'Rates Requested': 0,
  'Rates Received': 1,
  Quoting: 2,
  Booked: 3,
  'In Transit': 4,
  'Pre-arrival': 4,
  Delayed: 4,
  Arrived: 5,
  Completed: 6,
  Lost: 0,
  Cancelled: 0,
  Merged: 0,
};
export const stepOf = (stage: Stage) => STEP_OF[stage];

const place = (city: string, code: string, country: string): Place => ({ city, code, country });
const FRA = place('Frankfurt', 'FRA', 'Germany');
const DWC = place('Dubai', 'DWC', 'UAE');
const CAN = place('Guangzhou', 'CAN', 'China');
const AMM = place('Amman', 'AMM', 'Jordan');
const AQJ = place('Aqaba', 'AQJ', 'Jordan');

export const SHIPMENTS: Shipment[] = sampleOnly([
  {
    srb: 'SRB-711',
    customer: 'ABC Trading',
    mode: 'Air',
    service: 'Air Freight',
    from: CAN,
    to: AMM,
    stage: 'Rates Received',
    load: '2 packages · 222.25 kg · 1.2 CBM',
    cargo: 'General Cargo',
    incoterm: 'CPT',
    etd: '27 Jul 2025',
    eta: '2 Aug 2025',
    next: { text: 'Approve selling price', due: 'Today', tone: 'error' },
    messages: 2,
    updated: '2h ago',
    agent: 'Global Freight Ltd.',
    carrier: 'China Eastern Cargo',
    rollovers: 0,
    containers: 0,
    chargeableKg: 222.25,
    cbm: 0,
  },
  {
    srb: 'SRB-640',
    customer: 'Al-Rafedi',
    mode: 'Air',
    service: 'Air Freight',
    from: FRA,
    to: DWC,
    stage: 'In Transit',
    load: '3 packages · 746 kg · 1.2 CBM',
    cargo: 'General Cargo',
    incoterm: 'CPT',
    etd: '20 Jul 2025',
    eta: '28 Jul 2025',
    next: { text: 'Monitor arrival', due: '28 Jul 2025', tone: 'info' },
    messages: 1,
    updated: '4h ago',
    agent: 'Global Freight Ltd.',
    carrier: 'Emirates SkyCargo',
    rollovers: 0,
    containers: 0,
    chargeableKg: 746,
    cbm: 0,
    detail: {
      story:
        'The shipment was confirmed on 20 July 2025 and booked with Emirates SkyCargo. Cargo was collected from the supplier in Germany on 25 July and departed from Frankfurt on 27 July. The shipment is currently in transit to Dubai with an expected arrival on 28 July 2025. All documents have been received and no delays have been reported.',
      situation: [
        ['Current situation', 'In transit. Flight EK045 departed on 27 Jul. Shipment is on schedule.'],
        ['Next step', 'Monitor arrival and confirm delivery with overseas agent.'],
        ['No issues reported', 'Shipment is on track for the estimated arrival date.'],
      ],
      keyInfo: [
        ['SRB Code', 'SRB-640'],
        ['Customer', 'Al-Rafedi'],
        ['Route', 'Germany (FRA) → Dubai (DWC)'],
        ['Transport Mode', 'Air Freight'],
        ['Shipment Type', 'General Cargo'],
        ['Incoterm', 'CPT'],
        ['Cargo Details', '3 packages · 746 kg · 1.2 CBM'],
        ['Commodity', 'Kitchen equipment'],
        ['Created', '14 Jul 2025'],
        ['Last Updated', '27 Jul 2025, 14:30'],
      ],
      stepDates: ['15 Jul', '18 Jul', '20 Jul', '22 Jul', '27 Jul', '28 Jul (Est)', ''],
      timeline: [
        {
          when: '27 Jul 2025, 08:30',
          title: 'Departed Frankfurt (FRA)',
          place: 'EK045 · Emirates SkyCargo',
          tag: 'Completed',
          note: 'Flight departed on schedule. AWB: 176-7226 0025',
        },
        {
          when: '25 Jul 2025, 14:10',
          title: 'Collected from supplier',
          place: 'Frankfurt, Germany',
          tag: 'Completed',
          note: 'Cargo collected and delivered to Emirates terminal.',
        },
        {
          when: '22 Jul 2025, 11:45',
          title: 'Booking confirmed',
          place: 'EK045 · 27 Jul',
          tag: 'Completed',
          note: 'Booking confirmed with Emirates SkyCargo. Booking ref: 4E7K31',
        },
        {
          when: '20 Jul 2025, 16:22',
          title: 'Quotation approved by customer',
          place: 'USD 2,400',
          tag: 'Completed',
          note: 'Customer confirmed the quotation and approved to proceed.',
        },
        {
          when: '18 Jul 2025, 10:15',
          title: 'Received agent rates',
          place: 'Global Freight Ltd.',
          tag: 'Completed',
          note: 'Received final rates from agent.',
        },
      ],
      parties: [
        { role: 'Customer', name: 'Al-Rafedi', place: '' },
        { role: 'Shipper', name: 'Marmon Foodservice', place: 'Frankfurt, Germany' },
        { role: 'Consignee', name: 'Al-Rafedi', place: 'Dubai, UAE' },
        { role: 'Overseas Agent', name: 'Global Freight Ltd.', place: 'Dubai, UAE' },
        { role: 'Carrier', name: 'Emirates SkyCargo', place: 'EK045' },
      ],
      packages: [
        ['120 × 80 × 60', '250', 'Kitchen equipment'],
        ['120 × 80 × 60', '248', 'Kitchen equipment'],
        ['100 × 60 × 60', '248', 'Spare parts'],
      ],
      route: [
        ['Frankfurt (FRA) → Dubai (DWC)', ''],
        ['ETD (Actual)', '27 Jul 2025'],
        ['ETA (Estimated)', '28 Jul 2025'],
        ['Transit Time', '1 day'],
        ['Flight No.', 'EK045'],
        ['Carrier', 'Emirates SkyCargo'],
        ['Status', 'On schedule'],
      ],
    },
  },
  {
    srb: 'SRB-596',
    customer: 'Horizon Co.',
    mode: 'FCL',
    service: 'FCL · 40HC',
    from: place('Shanghai', 'PVG', 'China'),
    to: AQJ,
    stage: 'Booked',
    load: '12 pallets · 6,123 kg',
    cargo: 'General Cargo',
    incoterm: 'FOB',
    etd: '26 Jun 2025',
    eta: '5 Aug 2025',
    next: { text: 'Confirm BL', due: '5 Aug 2025', tone: 'info' },
    messages: 0,
    updated: '6h ago',
    agent: 'EuroTrans',
    carrier: 'CMA CGM',
    rollovers: 0,
    containers: 1,
    chargeableKg: 0,
    cbm: 0,
  },
  {
    srb: 'SRB-539',
    customer: 'TechnoPack',
    mode: 'LCL',
    service: 'LCL',
    from: place('Marseille', 'MRS', 'France'),
    to: AQJ,
    stage: 'Delayed',
    load: '14 pallets · 4,774 kg · 16.48 CBM',
    cargo: 'General Cargo',
    incoterm: 'FOB',
    etd: '9 Jun 2025',
    eta: '11 Jun 2025',
    next: { text: 'Contact agent', due: 'Overdue', tone: 'error' },
    messages: 3,
    updated: '1d ago',
    agent: 'Med Freight',
    carrier: 'Messina Line',
    rollovers: 1,
    containers: 0,
    chargeableKg: 0,
    cbm: 16.48,
  },
  {
    srb: 'SRB-703',
    customer: 'CMA Commercial',
    mode: 'FCL',
    service: 'FCL · 40HC',
    from: place('Jebel Ali', 'AEJEA', 'UAE'),
    to: AMM,
    stage: 'Pre-arrival',
    load: '1 container · 4,500 kg',
    cargo: 'General Cargo',
    incoterm: 'CIF',
    etd: '8 Aug 2025',
    eta: '12 Aug 2025',
    next: { text: 'Prepare JCS', due: '12 Aug 2025', tone: 'info' },
    messages: 0,
    updated: '1d ago',
    agent: 'Aqua Shipping',
    carrier: 'CMA CGM',
    rollovers: 1,
    containers: 1,
    chargeableKg: 0,
    cbm: 0,
  },
  {
    srb: 'SRB-625',
    customer: 'Sky Logistics',
    mode: 'LCL',
    service: 'LCL',
    from: place('Istanbul', 'IST', 'Turkey'),
    to: AQJ,
    stage: 'Rates Requested',
    load: '1 pallet · 140 kg · 0.8 CBM',
    cargo: 'General Cargo',
    incoterm: 'EXW',
    etd: '25 Jul 2025',
    eta: '—',
    next: { text: 'Wait for agent reply', due: '25 Jul 2025', tone: 'warning' },
    messages: 0,
    updated: '1d ago',
    agent: 'Sky Logistics',
    carrier: 'Arkas Line',
    rollovers: 0,
    containers: 0,
    chargeableKg: 0,
    cbm: 0.8,
  },
  {
    srb: 'SRB-518',
    customer: 'Al-Rafedi',
    mode: 'FCL',
    service: 'FCL · 40HC',
    from: place('Valencia', 'VLC', 'Spain'),
    to: AQJ,
    stage: 'Quoting',
    load: '1 container · 18,200 kg',
    cargo: 'Ceramic tiles',
    incoterm: 'FOB',
    etd: '26 Jul 2025',
    eta: '—',
    next: { text: 'Approve quotation', due: '26 Jul 2025', tone: 'warning' },
    messages: 0,
    updated: '1d ago',
    agent: 'EuroTrans',
    carrier: '',
    rollovers: 0,
    containers: 1,
    chargeableKg: 0,
    cbm: 0,
  },
  {
    srb: 'SRB-648',
    customer: 'TechnoPack',
    mode: 'Air',
    service: 'Air Freight',
    from: place('Milan', 'MXP', 'Italy'),
    to: AMM,
    stage: 'Quoting',
    load: '5 packages · 640 kg',
    cargo: 'Machine parts',
    incoterm: 'CPT',
    etd: '24 Jul 2025',
    eta: '—',
    next: { text: 'Review agent rates', due: '24 Jul 2025', tone: 'warning' },
    messages: 1,
    updated: '1d ago',
    agent: 'Global Freight Ltd.',
    carrier: '',
    rollovers: 0,
    containers: 0,
    chargeableKg: 640,
    cbm: 0,
  },
  {
    srb: 'SRB-701',
    customer: 'Horizon Co.',
    mode: 'FCL',
    service: 'FCL · 20GP',
    from: place('Hamburg', 'HAM', 'Germany'),
    to: AQJ,
    stage: 'Arrived',
    load: '1 container · 9,800 kg',
    cargo: 'General Cargo',
    incoterm: 'CIF',
    etd: '2 Jul 2025',
    eta: '21 Jul 2025',
    next: { text: 'Send invoice', due: '25 Jul 2025', tone: 'warning' },
    messages: 0,
    updated: '2d ago',
    agent: 'EuroTrans',
    carrier: 'Turkon Line',
    rollovers: 0,
    containers: 1,
    chargeableKg: 0,
    cbm: 0,
  },
  {
    srb: 'SRB-512',
    customer: 'ABC Trading',
    mode: 'Air',
    service: 'Air Freight',
    from: CAN,
    to: AMM,
    stage: 'Completed',
    load: '3 packages · 410 kg',
    cargo: 'General Cargo',
    incoterm: 'CPT',
    etd: '1 Jul 2025',
    eta: '4 Jul 2025',
    next: { text: 'Closed', due: '', tone: 'neutral' },
    messages: 0,
    updated: '3w ago',
    agent: 'Global Freight Ltd.',
    carrier: 'Royal Jordanian Cargo',
    rollovers: 0,
    containers: 0,
    chargeableKg: 410,
    cbm: 0,
  },
]);

/** Who should be chased, by the rule book: rates were requested, or a quotation was sent, and nothing has happened for the set number of days */
export function chaseOf(s: Shipment): { who: 'agent' | 'customer'; days: number } | null {
  if (!s.updatedOn) return null;
  const who = s.stage === 'Rates Requested' ? 'agent' : s.stage === 'Quoting' ? 'customer' : null;
  if (!who) return null;
  const days = daysSince(s.updatedOn);
  return days >= rule(who === 'agent' ? 'chaseAgentDays' : 'chaseCustomerDays') ? { who, days } : null;
}

/** A shipment the vault knows nothing about (yet): a conversation or quotation refers to its SRB, nothing else is known */
export const blankShipment = (srb: string, customer: string): Shipment => {
  const nowhere = { city: '–', code: '', country: '' };
  return {
    srb,
    customer: customer || '–',
    mode: 'Other',
    service: '–',
    from: nowhere,
    to: nowhere,
    stage: 'Quoting',
    load: '–',
    cargo: '–',
    incoterm: '–',
    etd: '–',
    eta: '–',
    next: { text: '', due: '', tone: 'neutral' },
    messages: 0,
    updated: '–',
    agent: '',
    carrier: '',
    rollovers: 0,
    containers: 0,
    chargeableKg: 0,
    cbm: 0,
  };
};

/** Still being worked on: not arrived, completed, lost, cancelled or merged */
export const isActive = (s: Shipment) => !['Arrived', 'Completed', 'Lost', 'Cancelled', 'Merged'].includes(s.stage);

export const needsAttention = (s: Shipment) =>
  s.stage === 'Delayed' || s.next.due === 'Overdue' || s.next.due === 'Today' || chaseOf(s) !== null;

/** Does a shipment belong under a tab? */
export const inTab = (tab: string, s: Shipment) =>
  tab === 'Active'
    ? isActive(s)
    : tab === 'Needs Attention'
      ? isActive(s) && needsAttention(s)
      : (STAGE_GROUPS.find((g) => g.label === tab)?.stages.includes(s.stage) ?? true);

/** The full page of a shipment. Shipments without hand-written detail get what we can work out from the list data. */
export function detailOf(s: Shipment): Detail {
  if (s.detail) return s.detail;
  const route = `${s.from.city} → ${s.to.city}`;
  return {
    story: `${s.srb} is a ${s.service.toLowerCase()} shipment for ${s.customer}, from ${s.from.city} to ${s.to.city}. It is at the "${s.stage}" stage. Next step: ${s.next.text.toLowerCase()}.`,
    situation: [
      ['Current situation', `Stage: ${s.stage}.`],
      ['Next step', s.next.text + '.'],
      ['Agent', s.agent],
    ],
    keyInfo: [
      ['SRB Code', s.srb],
      ['Customer', s.customer],
      ['Route', route],
      ['Transport Mode', s.service],
      ['Incoterm', s.incoterm],
      ['Cargo Details', s.load],
      ['Commodity', s.cargo],
      ['Last Updated', s.updated],
    ],
    stepDates: [],
    timeline: [],
    parties: [
      { role: 'Customer', name: s.customer, place: '' },
      { role: 'Overseas Agent', name: s.agent, place: '' },
    ],
    packages: [],
    route: [
      ['ETD', s.etd],
      ['ETA', s.eta],
    ],
  };
}

/** Pick-lists for forms, worked out from the shipments we know */
export const CUSTOMER_NAMES = [...new Set(SHIPMENTS.map((s) => s.customer))].sort();
export const PLACES = [...new Set(SHIPMENTS.flatMap((s) => [s.from, s.to]).map((p) => `${p.city} (${p.code})`))].sort();
export const CARGO_TYPES = [...new Set(SHIPMENTS.map((s) => s.cargo))].sort();
